import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { share } from 'rxjs/operators';

export interface IHandlers<TResponseBody> {
  success(data: TResponseBody);
  error(httpErrorResponse: HttpErrorResponse);
}

export interface IUploadHandlers<T> {
  event(value: HttpEvent<T>);
  error(httpErrorResponse: HttpErrorResponse);
}

export interface IGetOptions<TResponseBody> {
  uri: string;
  parameters?: HttpParams;
  handlers?: IHandlers<TResponseBody>;
  headers?: HttpHeaders;
}

export interface IPutOptions<TRequestBody, TResponseBody> {
  uri: string;
  parameters?: HttpParams;
  body: TRequestBody;
  handlers?: IHandlers<TResponseBody>;
}

export interface IPatchOptions<TRequestBody, TResponseBody> {
  uri: string;
  parameters?: HttpParams;
  body: TRequestBody;
  handlers?: IHandlers<TResponseBody>;
}

export interface IPostOptions<TRequestBody, TResponseBody> {
  uri: string;
  parameters?: HttpParams;
  body: TRequestBody;
  handlers?: IHandlers<TResponseBody>;
}

export interface IDeleteOptions<TResponseBody> {
  uri: string;
  parameters?: HttpParams;
  handlers?: IHandlers<TResponseBody>;
}

export interface IUploadOptions<TRequestBody, TResponseBody> {
  uri: string;
  parameters?: HttpParams;
  body: TRequestBody;
  handlers?: IUploadHandlers<TResponseBody>;
}

@Injectable()
export class RequestService {
  private apiUrl: string = environment.apiUrl;

  constructor(private httpClient: HttpClient) {}

  post<TRequestBody, TResponseBody>(
    options: IPostOptions<TRequestBody, TResponseBody>,
  ) {
    const observable = this.httpClient
      .post<TResponseBody>(this.apiUrl + options.uri, options.body, {
        withCredentials: true,
      })
      .pipe(share());
    observable.subscribe(options.handlers.success, options.handlers.error);
    return observable;
  }

  get<TResponseBody>(options: IGetOptions<TResponseBody>) {
    const observable = this.httpClient
      .get<TResponseBody>(this.apiUrl + options.uri, {
        params: options.parameters,
        withCredentials: true,
      })
      .pipe(share());
    if (options.handlers) {
      observable.subscribe(options.handlers.success, options.handlers.error);
    }
    return observable;
  }

  put<TRequestBody, TResponseBody>(
    options: IPutOptions<TRequestBody, TResponseBody>,
  ) {
    const observable = this.httpClient
      .put<TResponseBody>(this.apiUrl + options.uri, options.body, {
        withCredentials: true,
      })
      .pipe(share());
    observable.subscribe(options.handlers.success, options.handlers.error);
    return observable;
  }

  patch<TRequestBody, TResponseBody>(
    options: IPatchOptions<TRequestBody, TResponseBody>,
  ) {
    const observable = this.httpClient
      .patch<TResponseBody>(this.apiUrl + options.uri, options.body, {
        withCredentials: true,
      })
      .pipe(share());
    observable.subscribe(options.handlers.success, options.handlers.error);
    return observable;
  }

  delete<TResponseBody>(options: IDeleteOptions<TResponseBody>) {
    const observable = this.httpClient
      .delete<TResponseBody>(this.apiUrl + options.uri, {
        params: options.parameters,
        withCredentials: true,
      })
      .pipe(share());
    observable.subscribe(options.handlers.success, options.handlers.error);
    return observable;
  }

  upload<TRequestBody, TResponseBody>(
    formData: FormData,
    options: IPostOptions<TRequestBody, TResponseBody>,
  ) {
    const observable = this.httpClient
      .post<TResponseBody>(this.apiUrl + options.uri, formData, {
        withCredentials: true,
      })
      .pipe(share());

    observable.subscribe(options.handlers.success, options.handlers.error);
    return observable;
  }
}
